/**
 * GA4 Analytics Type Definitions
 *
 * This file defines the type system for the GA4 analytics implementation.
 * It provides type-safe event names, parameter keys, and parameter maps
 * to ensure consistent and correct event tracking across the application.
 *
 * @module types/analytics
 */

/** GA4 event parameter definition */
export interface GA4EventParams {
  [key: string]: string | number | boolean | undefined;
}

/** Standard parameter keys used across events */
export enum GA4ParamKey {
  /** Country code (e.g., "US", "KR", "PH") */
  CountryCode = "country_code",
  /** Whether the country has a local privacy site */
  HasLocalSite = "has_local_site",
  /** Request type: "download" | "delete" | "unsubscribe" | "all" */
  RequestType = "request_type",
  /** Request status: "all" | "requested" | "processing" | "completed" | "expired" | "canceled" */
  RequestStatus = "request_status",
  /** Service code (e.g., "CD30001") */
  ServiceCode = "service_code",
  /** Service display name (e.g., "Samsung Health") */
  ServiceName = "service_name",
  /** Navigation source: "gnb" | "drawer" | "hub" | "footer" | "header" */
  Source = "source",
  /** Action identifier within a section */
  Action = "action",
  /** Section/page where the event originated */
  Section = "section",
  /** Link URL for outbound links */
  LinkUrl = "link_url",
  /** Language code */
  Language = "language",
  /** Privacy policy version */
  PolicyVersion = "policy_version",
  /** Login method: "sa" | "email" | "china" */
  LoginMethod = "login_method",
  /** Load time in milliseconds */
  LoadTimeMs = "load_time_ms",
  /** Render time in milliseconds */
  RenderTimeMs = "render_time_ms",
  /** Web vital metric name */
  MetricName = "metric_name",
  /** Web vital metric value */
  MetricValue = "metric_value",
  /** Web vital metric rating: "good" | "needs-improvement" | "poor" */
  MetricRating = "metric_rating",
  /** Request number identifier */
  RequestNumber = "request_number",
  /** Page path */
  PagePath = "page_path",
  /** GEOIP-detected country code */
  GeoipCountry = "geoip_country",
  /** Site language country code */
  SiteLanguageCountry = "site_language_country",
  /** Samsung Account country code */
  SaCountry = "sa_country",
}

/** GA4 event names - new convention */
export enum GA4EventName {
  // Page & Navigation
  PageView = "page_view",
  PageLoad = "page_load",

  // Header & Navigation
  HeaderClick = "header_click",
  NavigationClick = "navigation_click",

  // Landing Page / Hub
  HubCountrySelect = "hub_country_select",
  HubMyDataClick = "hub_mydata_click",
  HubRequestHistoryClick = "hub_request_history_click",
  HubLocalPrivacyRedirect = "hub_local_privacy_redirect",
  HubPromptAction = "hub_prompt_action",
  HubPopupAction = "hub_popup_action",
  HubCountryChange = "hub_country_change",

  // GPS
  GpsCountryVisit = "gps_country_visit",
  GpsLinkClick = "gps_link_click",
  GpsSiteClick = "gps_site_click",

  // Authentication
  LoginAttempt = "login_attempt",
  Logout = "logout",
  OtpVerify = "otp_verify",
  OtpResend = "otp_resend",

  // Cookie Consent
  CookieConsent = "cookie_consent",

  // My Data
  MyDataAction = "my_data_action",
  RequestConfirmation = "request_confirmation",
  RequestDetailsAction = "request_details_action",

  // My Data History
  HistoryFilter = "history_filter",
  HistoryDownload = "history_download",

  // Privacy Policy
  PolicyVersionChange = "policy_version_change",
  PolicyDownload = "policy_download",

  // Footer
  FooterClick = "footer_click",

  // Language
  LanguageChange = "language_change",

  // Performance & Web Vitals
  WebVital = "web_vital",

  // Cookie Management
  CookieManage = "cookie_manage",
}

/** User property keys for GA4 */
export enum GA4UserPropertyKey {
  CountryCode = "country_code",
  HasLocalSite = "has_local_site",
  LoginType = "login_type",
  Language = "language",
  /** GEOIP-detected country code */
  GeoipCountry = "geoip_country",
  /** Site language country code */
  SiteLanguageCountry = "site_language_country",
  /** Samsung Account country code */
  SaCountry = "sa_country",
}

/** Type-safe event parameter map per event */
export type EventParamMap = {
  [GA4EventName.PageView]: { page_path: string; page_title?: string };
  [GA4EventName.PageLoad]: {
    page_path: string;
    load_time_ms: number;
    render_time_ms?: number;
  };
  [GA4EventName.HeaderClick]: {
    action: string;
    country_code?: string;
    source?: string;
  };
  [GA4EventName.NavigationClick]: {
    action: string;
    country_code?: string;
    source: string;
  };
  [GA4EventName.HubCountrySelect]: {
    country_code: string;
    has_local_site: boolean;
  };
  [GA4EventName.HubMyDataClick]: { country_code: string };
  [GA4EventName.HubRequestHistoryClick]: { country_code: string };
  [GA4EventName.HubLocalPrivacyRedirect]: {
    country_code: string;
    /** GEOIP-detected country code (also set as user property) */
    geoip_country?: string;
    /** Site language country code (also set as user property) */
    site_language_country?: string;
    /** Samsung Account country code (also set as user property) */
    sa_country?: string;
  };

  [GA4EventName.HubPromptAction]: { action: string; country_code: string };
  [GA4EventName.HubPopupAction]: { action: string; country_code: string };
  [GA4EventName.HubCountryChange]: {
    /** The country the user changed to */
    country_code: string;
    has_local_site: boolean;
    /** GEOIP-detected country code (also set as user property) */
    geoip_country?: string;
    /** Site language country code (also set as user property) */
    site_language_country?: string;
    /** Samsung Account country code (also set as user property) */
    sa_country?: string;
  };

  [GA4EventName.GpsCountryVisit]: { country_code: string };
  [GA4EventName.GpsLinkClick]: { country_code: string; link_url?: string };
  [GA4EventName.GpsSiteClick]: { country_code: string };
  [GA4EventName.LoginAttempt]: { login_method: string };
  [GA4EventName.Logout]: Record<string, never>;
  [GA4EventName.OtpVerify]: Record<string, never>;
  [GA4EventName.OtpResend]: Record<string, never>;
  [GA4EventName.CookieConsent]: { action: string };
  [GA4EventName.MyDataAction]: { action: string; country_code?: string };
  [GA4EventName.RequestConfirmation]: { request_type: string };
  [GA4EventName.RequestDetailsAction]: {
    action: string;
    source?: string;
    service_code?: string;
    service_name?: string;
    request_number?: string;
  };
  [GA4EventName.HistoryFilter]: {
    request_type: string;
    request_status: string;
    country_code?: string;
  };
  [GA4EventName.HistoryDownload]: { country_code?: string };
  [GA4EventName.PolicyVersionChange]: { policy_version?: string };
  [GA4EventName.PolicyDownload]: Record<string, never>;
  [GA4EventName.FooterClick]: { action: string };
  [GA4EventName.LanguageChange]: { language: string };
  [GA4EventName.WebVital]: {
    metric_name: string;
    metric_value: number;
    metric_rating: string;
  };
  [GA4EventName.CookieManage]: Record<string, never>;
};
